/**
 * useFloorPlanAnalysis Hook
 *
 * Provides functionality for uploading and analyzing floor plan images.
 * Handles file upload, AI analysis, and space creation from results.
 */

import { useCallback, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useQueryClient } from '@tanstack/react-query';
import { useApp } from '../contexts/AppContext';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../utils/errorLogging';
import { matchSpacesToMasterTypes, compareSpaces } from '../services/ai/utils';
import { TABLES } from '../constants';
import { pdfToImageUrl } from '../components/FloorPlan/Editor/pdfToImage';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];
const STORAGE_BUCKET = 'floor-plans';

/**
 * Floor Plan Analysis Hook
 * @param {Object} options - Hook options
 * @param {string} options.floorId - ID of the floor to analyze
 * @param {string} options.projectId - ID of the project
 * @returns {Object} Hook state and methods
 */
export const useFloorPlanAnalysis = ({ floorId, projectId } = {}) => {
  const supabase = useSupabaseClient();
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useApp();
  const { logError } = useErrorLogging();

  // State
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Validate file before upload
   */
  const validateFile = useCallback(file => {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`הקובץ ${file.name} גדול מדי. הגודל המקסימלי הוא 50MB`);
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      throw new Error(`סוג הקובץ ${file.type} אינו נתמך. נא להעלות קובץ PDF או תמונה.`);
    }
  }, []);

  /**
   * Convert file to base64
   */
  const fileToBase64 = useCallback(file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove data URL prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  /**
   * Upload file to storage
   */
  const uploadFile = useCallback(
    async (file, targetFloorId) => {
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${projectId}/${targetFloorId}/${timestamp}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        throw new Error(`שגיאה בהעלאת הקובץ: ${uploadError.message}`);
      }

      return filePath;
    },
    [supabase, projectId],
  );

  /**
   * Create analysis record in database
   */
  const createAnalysisRecord = useCallback(
    async (targetFloorId, filePath, fileType) => {
      const { data, error: insertError } = await supabase
        .from('floor_plan_analyses')
        .insert({
          floor_id: targetFloorId,
          file_path: filePath,
          file_type: fileType.includes('pdf') ? 'pdf' : 'image',
          status: 'processing',
        })
        .select()
        .single();

      if (insertError) {
        throw new Error(`שגיאה ביצירת רשומת ניתוח: ${insertError.message}`);
      }

      return data;
    },
    [supabase],
  );

  /**
   * Call Edge Function to analyze floor plan
   */
  const callAnalysisFunction = useCallback(
    async (imageBase64, mimeType, masterSpaces, analysisId) => {
      const { data, error: fnError } = await supabase.functions.invoke('analyze-floor-plan', {
        body: {
          imageBase64,
          mimeType,
          masterSpaces,
          floorId,
          analysisId,
        },
      });

      if (fnError) {
        // fnError.context is the raw Response object — read the body to get the real error
        let detail = fnError.message;
        try {
          const text = await fnError.context?.text();
          console.error('[Floor Plan Analysis] Raw response body:', text);
          if (text) {
            const body = JSON.parse(text);
            detail = body?.error || detail;
          }
        } catch (_) {
          // response body may be empty (e.g., timeout) or not JSON
        }
        console.error('[Floor Plan Analysis] Edge function error:', detail);
        throw new Error(`שגיאה בניתוח התוכנית: ${detail}`);
      }

      if (!data.success) {
        console.error('[Floor Plan Analysis] Function returned error:', data.error);
        throw new Error(data.error || 'שגיאה לא ידועה בניתוח התוכנית');
      }

      return data.data;
    },
    [supabase, floorId],
  );

  /**
   * Update floor record with floor plan path
   */
  const updateFloorWithPlan = useCallback(
    async (targetFloorId, filePath) => {
      const { error: updateError } = await supabase
        .from('project_floors')
        .update({
          floor_plan_path: filePath,
          floor_plan_uploaded_at: new Date().toISOString(),
        })
        .eq('id', targetFloorId);

      if (updateError) {
        console.error('Error updating floor plan path:', updateError);
      }
    },
    [supabase],
  );

  /**
   * Fetch master spaces for matching
   */
  const fetchMasterSpaces = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from('master_spaces')
      .select('id, name, space_category');

    if (fetchError) {
      console.error('Error fetching master spaces:', fetchError);
      return [];
    }

    return data || [];
  }, [supabase]);

  /**
   * Fetch existing spaces for the floor (for merge comparison)
   * Only fetches non-deleted spaces
   */
  const fetchExistingSpaces = useCallback(
    async targetFloorId => {
      const { data, error: fetchError } = await supabase
        .from('project_spaces')
        .select('id, custom_space_name, room_size, master_space_id')
        .eq('floor_id', targetFloorId)
        .is('deleted_at', null);

      if (fetchError) {
        console.error('Error fetching existing spaces:', fetchError);
        return [];
      }

      return data || [];
    },
    [supabase],
  );

  /**
   * Main analyze function
   */
  const analyzeFloorPlan = useCallback(
    async (file, targetFloorId = floorId) => {
      if (!targetFloorId || !projectId) {
        throw new Error('Floor ID and Project ID are required');
      }

      setError(null);
      setIsUploading(true);
      setUploadProgress(0);

      try {
        // Validate file
        validateFile(file);

        // Upload original file to storage (preserves the original PDF/image)
        setUploadProgress(20);
        const filePath = await uploadFile(file, targetFloorId);

        // Convert to base64 for analysis. PDFs need to be rendered to PNG
        // first because both Gemini and the Python CV service expect images.
        setUploadProgress(35);
        let analysisBase64;
        let analysisMimeType;
        if (file.type === 'application/pdf') {
          const fileUrl = URL.createObjectURL(file);
          try {
            const dataUrl = await pdfToImageUrl(fileUrl);
            // dataUrl is "data:image/png;base64,..."
            analysisBase64 = dataUrl.split(',')[1];
            analysisMimeType = 'image/png';
          } finally {
            URL.revokeObjectURL(fileUrl);
          }
        } else {
          analysisBase64 = await fileToBase64(file);
          analysisMimeType = file.type;
        }

        // Create analysis record
        setUploadProgress(50);
        const analysisRecord = await createAnalysisRecord(targetFloorId, filePath, file.type);

        setIsUploading(false);
        setIsAnalyzing(true);

        // Fetch master spaces for matching
        setUploadProgress(60);
        const masterSpaces = await fetchMasterSpaces();

        // Call analysis function with the rendered PNG (or original image)
        setUploadProgress(70);
        const result = await callAnalysisFunction(
          analysisBase64,
          analysisMimeType,
          masterSpaces,
          analysisRecord.id,
        );

        // Match detected spaces to master space types
        setUploadProgress(90);
        const matchedSpaces = matchSpacesToMasterTypes(result.spaces, masterSpaces);

        // Fetch existing spaces for comparison
        const existingSpaces = await fetchExistingSpaces(targetFloorId);
        const comparison = compareSpaces(matchedSpaces, existingSpaces);

        // Update floor with plan path
        await updateFloorWithPlan(targetFloorId, filePath);

        setUploadProgress(100);

        const finalResult = {
          analysisId: analysisRecord.id,
          filePath,
          spaces: matchedSpaces,
          windows: result.windows || [],
          metadata: result.metadata,
          comparison,
          existingSpaces,
        };

        setAnalysisResult(finalResult);
        setIsAnalyzing(false);

        return finalResult;
      } catch (err) {
        setError(err.message);
        setIsUploading(false);
        setIsAnalyzing(false);

        logError(err, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.API,
          message: 'Floor plan analysis failed',
          context: { floorId: targetFloorId, projectId },
        });

        throw err;
      }
    },
    [
      floorId,
      projectId,
      validateFile,
      fileToBase64,
      uploadFile,
      createAnalysisRecord,
      fetchMasterSpaces,
      callAnalysisFunction,
      fetchExistingSpaces,
      updateFloorWithPlan,
      logError,
    ],
  );

  /**
   * Create spaces from accepted suggestions (legacy - for simple creates only)
   */
  const createSpacesFromAnalysis = useCallback(
    async (analysisId, acceptedSpaces) => {
      try {
        const { data, error: rpcError } = await supabase.rpc('create_spaces_from_analysis', {
          p_analysis_id: analysisId,
          p_accepted_spaces: acceptedSpaces,
        });

        if (rpcError) {
          throw new Error(`שגיאה ביצירת החללים: ${rpcError.message}`);
        }

        if (!data.success) {
          throw new Error(data.error || 'שגיאה לא ידועה ביצירת החללים');
        }

        // Invalidate project_spaces queries to trigger refetch with joined data
        queryClient.invalidateQueries({ queryKey: [TABLES.PROJECT_SPACES] });

        showSuccess(`נוצרו ${data.created_count} חללים בהצלחה`);
        return data;
      } catch (err) {
        showError(err.message);
        logError(err, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.API,
          message: 'Failed to create spaces from analysis',
          context: { analysisId, spacesCount: acceptedSpaces?.length },
        });
        throw err;
      }
    },
    [supabase, queryClient, showSuccess, showError, logError],
  );

  /**
   * Merge spaces from analysis (full merge with create/update/delete)
   * @param {string} analysisId - The analysis record ID
   * @param {Object[]} spacesToCreate - New spaces to create
   * @param {Object[]} spacesToUpdate - Existing spaces to update with new dimensions
   * @param {string[]} spaceIdsToDelete - IDs of spaces to soft-delete
   * @param {Object[]} windowsToCreate - Windows to create
   */
  const mergeSpacesFromAnalysis = useCallback(
    async (
      analysisId,
      spacesToCreate = [],
      spacesToUpdate = [],
      spaceIdsToDelete = [],
      windowsToCreate = [],
    ) => {
      try {
        const { data, error: rpcError } = await supabase.rpc('merge_spaces_from_analysis', {
          p_analysis_id: analysisId,
          p_spaces_to_create: spacesToCreate,
          p_spaces_to_update: spacesToUpdate,
          p_space_ids_to_delete: spaceIdsToDelete,
          p_windows_to_create: windowsToCreate,
        });

        if (rpcError) {
          throw new Error(`שגיאה במיזוג החללים: ${rpcError.message}`);
        }

        if (!data.success) {
          throw new Error(data.error || 'שגיאה לא ידועה במיזוג החללים');
        }

        // Invalidate project_spaces and project_windows queries to trigger refetch
        queryClient.invalidateQueries({ queryKey: [TABLES.PROJECT_SPACES] });
        queryClient.invalidateQueries({ queryKey: [TABLES.PROJECT_WINDOWS] });

        // Build success message
        const messages = [];
        if (data.created_count > 0) messages.push(`נוצרו ${data.created_count} חללים`);
        if (data.updated_count > 0) messages.push(`עודכנו ${data.updated_count} חללים`);
        if (data.deleted_count > 0) messages.push(`הוסרו ${data.deleted_count} חללים`);
        if (data.windows_created_count > 0)
          messages.push(`נוצרו ${data.windows_created_count} חלונות`);

        if (messages.length > 0) {
          showSuccess(messages.join(', '));
        } else {
          showSuccess('לא בוצעו שינויים');
        }

        return data;
      } catch (err) {
        showError(err.message);
        logError(err, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.API,
          message: 'Failed to merge spaces from analysis',
          context: {
            analysisId,
            createCount: spacesToCreate?.length,
            updateCount: spacesToUpdate?.length,
            deleteCount: spaceIdsToDelete?.length,
            windowsCount: windowsToCreate?.length,
          },
        });
        throw err;
      }
    },
    [supabase, queryClient, showSuccess, showError, logError],
  );

  /**
   * Get signed URL for floor plan image
   */
  const getFloorPlanUrl = useCallback(
    async filePath => {
      if (!filePath) return null;

      const { data, error: urlError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(filePath, 60 * 60); // 1 hour

      if (urlError) {
        console.error('Error getting signed URL:', urlError);
        return null;
      }

      return data.signedUrl;
    },
    [supabase],
  );

  /**
   * Re-analyze an existing floor plan from storage path
   * This skips the upload step and uses the already-uploaded file
   */
  const reanalyzeFromPath = useCallback(
    async (existingFilePath, targetFloorId = floorId) => {
      if (!targetFloorId || !projectId || !existingFilePath) {
        throw new Error('Floor ID, Project ID, and existing file path are required');
      }

      setError(null);
      setIsAnalyzing(true);
      setUploadProgress(10);

      try {
        // Download the existing file from storage to get its content
        setUploadProgress(20);
        const { data: fileData, error: downloadError } = await supabase.storage
          .from(STORAGE_BUCKET)
          .download(existingFilePath);

        if (downloadError) {
          throw new Error(`שגיאה בטעינת הקובץ: ${downloadError.message}`);
        }

        // Convert blob to base64 via FileReader (avoids reduce-over-Uint8Array
        // which can crash the tab on large PDFs).
        setUploadProgress(30);
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(fileData);
        });

        // Determine mime type from file path
        const ext = existingFilePath.split('.').pop()?.toLowerCase();
        const mimeType =
          ext === 'pdf'
            ? 'application/pdf'
            : ext === 'png'
              ? 'image/png'
              : ext === 'jpg' || ext === 'jpeg'
                ? 'image/jpeg'
                : 'image/png';

        // Create analysis record
        setUploadProgress(50);
        const analysisRecord = await createAnalysisRecord(
          targetFloorId,
          existingFilePath,
          mimeType,
        );

        // Fetch master spaces for matching
        setUploadProgress(60);
        const masterSpaces = await fetchMasterSpaces();

        // Call analysis function
        setUploadProgress(70);
        const result = await callAnalysisFunction(
          base64,
          mimeType,
          masterSpaces,
          analysisRecord.id,
        );

        // Match detected spaces to master space types
        setUploadProgress(90);
        const matchedSpaces = matchSpacesToMasterTypes(result.spaces, masterSpaces);

        // Fetch existing spaces for comparison
        const existingSpaces = await fetchExistingSpaces(targetFloorId);
        const comparison = compareSpaces(matchedSpaces, existingSpaces);

        setUploadProgress(100);

        const finalResult = {
          analysisId: analysisRecord.id,
          filePath: existingFilePath,
          spaces: matchedSpaces,
          windows: result.windows || [],
          metadata: result.metadata,
          comparison,
          existingSpaces,
        };

        setAnalysisResult(finalResult);
        setIsAnalyzing(false);

        return finalResult;
      } catch (err) {
        setError(err.message);
        setIsAnalyzing(false);

        logError(err, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.API,
          message: 'Floor plan re-analysis failed',
          context: { floorId: targetFloorId, projectId, existingFilePath },
        });

        throw err;
      }
    },
    [
      floorId,
      projectId,
      supabase,
      createAnalysisRecord,
      fetchMasterSpaces,
      callAnalysisFunction,
      fetchExistingSpaces,
      logError,
    ],
  );

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setIsUploading(false);
    setIsAnalyzing(false);
    setUploadProgress(0);
    setAnalysisResult(null);
    setError(null);
  }, []);

  return {
    // State
    isUploading,
    isAnalyzing,
    isProcessing: isUploading || isAnalyzing,
    uploadProgress,
    analysisResult,
    error,

    // Methods
    analyzeFloorPlan,
    reanalyzeFromPath,
    createSpacesFromAnalysis,
    mergeSpacesFromAnalysis,
    getFloorPlanUrl,
    reset,
  };
};
